import React, {
    ForwardedRef,
    ChangeEvent,
    KeyboardEvent,
    MouseEvent,
    forwardRef
} from 'react';
import styled, { css } from 'styled-components';
import { lighten, darken } from 'polished';
import theme from '@styles/theme';
import {
    BoldFont,
    Description,
    RegularFont,
    FlexBox
} from './StyledComponents';
import StyledButton from './StyledButton';

type StyledInputProps = {
    MAX_LENGTH?: boolean;
    WITH_BUTTON?: boolean;
    HIDE_COUNT?: boolean;
    REQUIRED?: boolean;
    PLACEHOLDER?: boolean;
    COLUMN?: boolean;
    width?: number;
    height?: number;
    margin?: string;
    border?: string;
    borderRadius?: number;
    title?: string;
    subTitle?: string;
    description?: string;
    name?: string;
    value: string;
    type?: string;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    onWheel?: (e: MouseEvent<HTMLInputElement>) => void;
    onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
    onKeyUp?: (e: KeyboardEvent<HTMLInputElement>) => void;
    maxLength?: number;
    disabled?: boolean;
    placeholder?: string;
    fontColor?: string;
    buttonTitle?: string;
    buttonBgColor?: string;
    buttonFontColor?: string;
};

const StyledInput = forwardRef(
    (
        {
            MAX_LENGTH,
            WITH_BUTTON,
            REQUIRED,
            PLACEHOLDER,
            COLUMN,
            width,
            height = 50,
            margin,
            border,
            borderRadius = 4,
            title,
            subTitle,
            description,
            name = '',
            value = '',
            type = 'text',
            onChange = () => null,
            onWheel = () => null,
            onKeyUp = () => null,
            onKeyDown = () => null,
            maxLength,
            HIDE_COUNT,
            disabled = false,
            placeholder = '',
            fontColor,
            buttonTitle,
            buttonBgColor,
            buttonFontColor
        }: StyledInputProps,
        ref: ForwardedRef<HTMLInputElement>
    ) => {
        if (COLUMN) {
            return (
                <Wrapper $column={COLUMN} $width={width} $margin={margin}>
                    {title && (
                        <FlexBox
                            $justifyContent="space-between"
                            $alignItems="center"
                        >
                            <ColumnTitle>
                                {title}
                                {REQUIRED && <Asterisk> *</Asterisk>}
                                {subTitle && <SubTitle>{subTitle}</SubTitle>}
                            </ColumnTitle>
                            {MAX_LENGTH && (
                                <CountBox
                                    $hide={HIDE_COUNT}
                                >{`(${value.length} / ${maxLength})`}</CountBox>
                            )}
                        </FlexBox>
                    )}
                    <Input
                        $placeholder={PLACEHOLDER}
                        $withButton={WITH_BUTTON}
                        $height={height}
                        $border={border}
                        $borderRadius={borderRadius}
                        ref={ref}
                        name={name}
                        value={value}
                        type={type}
                        onChange={onChange}
                        onWheel={onWheel}
                        onKeyUp={onKeyUp}
                        onKeyDown={onKeyDown}
                        maxLength={maxLength}
                        disabled={disabled}
                        placeholder={placeholder}
                    />
                    {WITH_BUTTON && (
                        <Button
                            $bgColor={buttonBgColor}
                            $fontColor={buttonFontColor}
                        >
                            {buttonTitle}
                        </Button>
                    )}
                    {description && (
                        <DescriptionBox>
                            <Description $fontColor={fontColor}>
                                {description}
                            </Description>
                        </DescriptionBox>
                    )}
                </Wrapper>
            );
        }

        return (
            <Wrapper $column={COLUMN} $margin={margin}>
                {title && <RowTitle>{title}</RowTitle>}

                <Input
                    $placeholder={PLACEHOLDER}
                    $width={width}
                    $height={height}
                    $border={border}
                    $column={COLUMN}
                    $withButton={WITH_BUTTON}
                    $borderRadius={borderRadius}
                    ref={ref}
                    name={name}
                    value={value}
                    type={type}
                    onChange={onChange}
                    onWheel={onWheel}
                    onKeyUp={onKeyUp}
                    onKeyDown={onKeyDown}
                    maxLength={maxLength}
                    disabled={disabled}
                    placeholder={placeholder}
                />
                {WITH_BUTTON && (
                    <Button
                        $bgColor={buttonBgColor}
                        $fontColor={buttonFontColor}
                    >
                        {buttonTitle}
                    </Button>
                )}
                {description && (
                    <DescriptionBox>
                        <Description $fontColor={fontColor}>
                            {description}
                        </Description>
                    </DescriptionBox>
                )}
            </Wrapper>
        );
    }
);

export default React.memo(StyledInput);

const Wrapper = styled.label<{
    $column?: boolean;
    $width?: number;
    $margin?: string;
}>`
    width: ${({ $width }) => ($width ? `${$width}px` : '100%')};
    margin: ${({ $margin }) => ($margin ? $margin : 0)};
    display: flex;
    align-items: center;
    position: relative;

    ${({ $column }) =>
        $column &&
        css`
            display: block;
        `};
`;

const ColumnTitle = styled(RegularFont)`
    margin-bottom: 8px;
    font-size: 14px;
`;

const RowTitle = styled(BoldFont)`
    width: 180px;
`;

const Asterisk = styled.span`
    color: ${theme.colors.activeOrange};
`;

const SubTitle = styled.span`
    margin-left: 12px;
    color: ${theme.colors.lightGrayFontColor};
`;

const Input = styled.input<{
    $placeholder?: boolean;
    $width?: number;
    $height?: number;
    $border?: string;
    $column?: boolean;
    $withButton?: boolean;
    $borderRadius?: number;
}>`
    padding: ${({ $withButton }) =>
        $withButton ? '14px 74px 14px 12px' : '14px 12px'};
    width: ${({ $width }) => ($width ? `${$width}px` : '100%')};
    height: ${({ $height }) => $height}px;
    font-size: 15px;
    font-family: PretendardRegular, sans-serif;
    border: ${({ $border }) =>
        $border ? $border : `1px solid ${theme.colors.lightGrayBorder}`};
    border-radius: ${({ $borderRadius }) => $borderRadius}px;

    &:disabled {
        /* border: none; */
    }

    &::placeholder {
        font-size: 14px;
        font-family: PretendardRegular, sans-serif;
        color: ${theme.colors.deepGrayFontColor};
    }

    ${({ $placeholder }) =>
        $placeholder &&
        css`
            font-size: 20px;
            font-family: PretendardMedium, sans-serif;

            &::placeholder {
                font-size: 20px;
                font-family: PretendardRegular, sans-serif;
                color: ${theme.colors.deepGrayFontColor};
            }
        `};
`;

const DescriptionBox = styled.div<{ $column?: boolean }>`
    width: 100%;
    ${({ $column }) =>
        $column &&
        css`
            margin-left: 180px;
            width: calc(100% - 180px);
        `};
`;

const CountBox = styled.span<{ $hide?: boolean }>`
    display: ${({ $hide }) => ($hide ? 'none' : 'inline-block')};
    margin-bottom: 8px;
    color: ${theme.colors.deepGrayFontColor};
    font-size: 14px;
`;

const Button = styled.button<{ $fontColor?: string; $bgColor?: string }>`
    position: absolute;
    bottom: 8px;
    right: 12px;
    color: ${({ $fontColor }) =>
        $fontColor ? $fontColor : theme.colors.whiteColor};
    background-color: ${({ $bgColor }) =>
        $bgColor ? $bgColor : theme.colors.blackColor};
    padding: 7px 12px;
    border-radius: 4px;

    &:hover {
        background-color: ${({ $bgColor }) =>
            $bgColor === theme.colors.blackColor
                ? lighten(0.15, theme.colors.blackColor)
                : darken(0.1, $bgColor ?? theme.colors.blackColor)};
    }
`;
